import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  classifyPhoto,
  GalleryLightbox,
  type GalleryImage,
} from './_galleryShared';

/**
 * Single staggered photo gallery that combines apartment + community shots
 * from `photo_gallery` into one asymmetric mosaic. Inspired by
 * lakehausapartmenthomes.com/gallery — tiles cycle through a repeating
 * 8-image pattern on desktop that alternates a 2x2 "hero" tile between the
 * left and right sides of the grid, with mixed 1x1 and 2x1 tiles filling
 * around it. Clicking any tile opens the shared lightbox.
 *
 * Because every photo in `photo_gallery` is delivered as 16:9, a pure
 * column-flow masonry layout collapses to a uniform grid — we instead use
 * CSS Grid with explicit row/column spans to force the staggered look.
 */
export function GalleryStaggered() {
  const images = useCollectionBinding<GalleryImage>('gallerystaggered.images', {
    collection: 'photo_gallery',
  });

  // Keep apartment + community shots, drop unclassified marketing/other
  // photos so the grid stays cohesive. If everything is "other" (e.g. a
  // property whose titles aren't tagged), fall back to all photos so the
  // gallery never collapses to empty.
  const photos = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const tagged = list.filter((img) => {
      const kind = classifyPhoto(img);
      return kind === 'apartment' || kind === 'community';
    });
    return tagged.length > 0 ? tagged : list;
  }, [images]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Mobile single-image carousel state. Mobile users see one image at a
  // time with prev/next + swipe — the same UX as the expanded lightbox,
  // but rendered inline so it doesn't require a tap-to-open step. The
  // staggered desktop grid still shows on >= 640px viewports.
  const total = photos.length;
  const [mobileIndex, setMobileIndex] = useState(0);
  const safeMobileIndex = total > 0 ? ((mobileIndex % total) + total) % total : 0;
  const mobilePhoto = total > 0 ? photos[safeMobileIndex] : null;

  const mobileNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      setMobileIndex((i) => i + 1);
    },
    [],
  );
  const mobilePrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      setMobileIndex((i) => i - 1);
    },
    [],
  );

  // Mobile swipe — same horizontal-only gesture model as the lightbox.
  const mobileTouchStart = useRef<{ x: number; y: number } | null>(null);
  const handleMobileTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    mobileTouchStart.current = { x: t.clientX, y: t.clientY };
  }, []);
  const handleMobileTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = mobileTouchStart.current;
      mobileTouchStart.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < 40) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) mobileNext();
      else mobilePrev();
    },
    [mobileNext, mobilePrev],
  );

  return (
    <section className="bg-background">
      <div className="gallery-staggered__wrap">
        {photos.length === 0 ? (
          <p className="gallery-staggered__empty">
            Gallery images will be displayed here.
          </p>
        ) : (
          <>
            {/* Mobile-only single-image carousel (hidden at >= 640px). */}
            {mobilePhoto && (
              <div
                className="gallery-staggered__mobile"
                onTouchStart={handleMobileTouchStart}
                onTouchEnd={handleMobileTouchEnd}
              >
                <button
                  type="button"
                  className="gallery-staggered__mobile-img-btn"
                  onClick={() => setSelectedIndex(safeMobileIndex)}
                  aria-label={`Open photo ${safeMobileIndex + 1} of ${total} in lightbox`}
                >
                  <VibesitesImage
                    url={mobilePhoto.image || mobilePhoto.original_url || ''}
                    alt={mobilePhoto.alt_text || mobilePhoto.title || ''}
                    className="gallery-staggered__mobile-img"
                  />
                </button>

                {total > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={mobilePrev}
                      className="gallery-staggered__mobile-arrow gallery-staggered__mobile-arrow--prev"
                      aria-label="Previous image"
                    >
                      &#8249;
                    </button>
                    <button
                      type="button"
                      onClick={mobileNext}
                      className="gallery-staggered__mobile-arrow gallery-staggered__mobile-arrow--next"
                      aria-label="Next image"
                    >
                      &#8250;
                    </button>
                    <div
                      className="gallery-staggered__mobile-counter"
                      aria-live="polite"
                    >
                      {safeMobileIndex + 1} / {total}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Desktop / tablet staggered grid (hidden below 640px). */}
            <div className="gallery-staggered__grid">
              {photos.map((img, index) => (
                <button
                  key={`${img.image || index}-${index}`}
                  type="button"
                  className="gallery-staggered__item"
                  onClick={() => setSelectedIndex(index)}
                  aria-label={`Open photo ${index + 1} in lightbox`}
                >
                  <VibesitesImage
                    url={img.image || img.original_url || ''}
                    alt={img.alt_text || img.title || ''}
                    className="gallery-staggered__img"
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <GalleryLightbox
        images={photos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .gallery-staggered__wrap {
          width: 96vw;
          max-width: 96vw;
          margin-left: auto;
          margin-right: auto;
          padding-top: 1rem;
          padding-bottom: 1rem;
        }

        @media (min-width: 768px) {
          .gallery-staggered__wrap {
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
          }
        }

        .gallery-staggered__empty {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 3rem 0;
        }

        /* Mobile single-image carousel — one full-width photo at a time
           with prev/next + swipe. The source photos are 16:9, so we use
           that aspect ratio and cover-fit to avoid any letterboxing.
           Shown only below 640px; the grid takes over at tablet+. */
        .gallery-staggered__mobile {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          touch-action: pan-y;
          overflow: hidden;
        }

        .gallery-staggered__mobile-img-btn {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          cursor: pointer;
        }

        .gallery-staggered__mobile-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          animation: gallery-staggered-fade-in 0.3s ease both;
        }

        .gallery-staggered__mobile-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: #fff;
          font-size: 2.75rem;
          line-height: 1;
          background: rgba(0, 0, 0, 0.35);
          border: none;
          cursor: pointer;
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
          opacity: 0.85;
          z-index: 2;
          -webkit-user-select: none;
          user-select: none;
          transition: opacity 0.15s, background 0.15s;
        }
        .gallery-staggered__mobile-arrow:hover {
          opacity: 1;
          background: rgba(0, 0, 0, 0.6);
        }
        .gallery-staggered__mobile-arrow--prev { left: 0.25rem; }
        .gallery-staggered__mobile-arrow--next { right: 0.25rem; }

        .gallery-staggered__mobile-counter {
          position: absolute;
          bottom: 0.75rem;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.8125rem;
          letter-spacing: 0.05em;
          pointer-events: none;
          background: rgba(0, 0, 0, 0.35);
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
        }

        /* Hide the mobile carousel at tablet+ — the staggered grid is the
           desktop/tablet experience. */
        @media (min-width: 640px) {
          .gallery-staggered__mobile {
            display: none;
          }
        }

        /* Asymmetric CSS-Grid mosaic. Tiles use explicit col/row spans to
           produce the staggered pattern; grid-auto-flow:dense lets later
           1x1 tiles back-fill any holes when the photo count isn't an exact
           multiple of the 8-tile block. */
        .gallery-staggered__grid {
          display: none;
          gap: 0.25rem;
          grid-template-columns: 1fr;
          grid-auto-rows: 70vw;
          grid-auto-flow: dense;
        }

        /* Tablet: 2 columns with a simple 4-tile stagger pattern. */
        @media (min-width: 640px) {
          .gallery-staggered__grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: 30vw;
          }
          /* every 4 tiles: tile 1 is a tall 1x2, the next three are 1x1
             — gives a clear staggered offset between the two columns. */
          .gallery-staggered__item:nth-child(4n + 1) {
            grid-row: span 2;
          }
        }

        /* Desktop: 4-column mosaic. Repeating 8-tile pattern:
           [ 1 1 2 3 ]
           [ 1 1 4 4 ]
           [ 5 6 7 7 ]
           [ 8 8 7 7 ]
           — big 2x2 tile alternates between the left and right halves. */
        @media (min-width: 1024px) {
          .gallery-staggered__grid {
            grid-template-columns: repeat(4, 1fr);
            grid-auto-rows: 16vw;
          }
          /* Reset the tablet rule first so it doesn't leak into desktop. */
          .gallery-staggered__item:nth-child(4n + 1) {
            grid-row: auto;
          }
          /* 2x2 hero tiles — positions 1 and 7 in the 8-tile cycle. */
          .gallery-staggered__item:nth-child(8n + 1),
          .gallery-staggered__item:nth-child(8n + 7) {
            grid-column: span 2;
            grid-row: span 2;
          }
          /* 2x1 wide tiles — positions 4 and 8 in the 8-tile cycle. */
          .gallery-staggered__item:nth-child(8n + 4),
          .gallery-staggered__item:nth-child(8n + 8) {
            grid-column: span 2;
            grid-row: span 1;
          }
        }

        @media (min-width: 1400px) {
          .gallery-staggered__grid {
            grid-auto-rows: 15vw;
          }
        }

        .gallery-staggered__item {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          cursor: pointer;
          overflow: hidden;
          animation: gallery-staggered-fade-in 0.4s ease both;
          transition: box-shadow 0.3s ease;
        }

        .gallery-staggered__item:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
        }

        .gallery-staggered__item:focus-visible {
          outline: 2px solid var(--vibe-color-accent);
          outline-offset: 2px;
        }

        .gallery-staggered__img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-staggered__item:hover .gallery-staggered__img {
          transform: scale(1.04);
        }

        @keyframes gallery-staggered-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-staggered__item,
          .gallery-staggered__img {
            animation: none;
            transition: none;
          }
          .gallery-staggered__item:hover .gallery-staggered__img {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryStaggered',
  displayName: 'Staggered Photo Gallery',
  // No editable content tokens — every photo is rendered through the
  // photo_gallery collection binding. The field is still required by
  // ComponentContentSchema, and the editor iterates it with
  // Object.entries(schema.tokens) when the section is selected, so it must
  // be a real object rather than undefined.
  tokens: {},
  collectionBindings: {
    'gallerystaggered.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Pulled from the property photo gallery. Apartment + community shots are shown together in a single asymmetric staggered mosaic.',
    },
  },
};

(GalleryStaggered as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryStaggered;
